import React, { useRef, useState, useEffect } from "react";
import styles from "./Registration.module.css";
import ReCAPTCHA from "react-google-recaptcha";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import SimpleLoader from "../../components/SimpleLoader/SimpleLoader";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  showErrorToastNotification,
  showSuccessToastNotification,
  showWarnToastNotification,
} from "../../components/ToastNotification/ToastNotification";
import { validateRegForm } from "../../validators/regForm";
import FormField from "../../components/FormField/FormField";
import RegistrationInfoPane from "./RegistrationInfoPane";
import { apiRegisterUser } from "../../api/registerUser";

// Icons
import nameIcon from "../../assets/icons/name.png";
import deptIcon from "../../assets/icons/department.png";
import yearIcon from "../../assets/icons/year.png";
import collegeIcon from "../../assets/icons/college.png";
import emailIcon from "../../assets/icons/email.png";
import phoneIcon from "../../assets/icons/phone.png";
// You might want a generic 'description' icon or reuse an existing one
import descriptionIcon from "../../assets/icons/college.png";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Define initial states for different forms
const studentFormFormat = {
  name: "",
  year: 1,
  dept: "",
  college: "",
  email: "",
  phone: "",
  workshopsandevents: "",
};

const partnerFormFormat = {
  name: "",
  description: "",
  email: "",
  phone: "",
};

const Registration = () => {
  ScrollToTop();

  // State for switching form types: 'student', 'college', 'sponsor', 'company'
  const [registrationType, setRegistrationType] = useState("student");

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentReq, setPaymentReq] = useState(false); // Logic can be adjusted based on needs

  // Initialize form state based on current type
  const [formState, setFormState] = useState(studentFormFormat);

  // Update form state structure when registration type changes
  useEffect(() => {
    if (registrationType === "student") {
      setFormState(studentFormFormat);
    } else {
      setFormState(partnerFormFormat);
    }
    setFile(null); // Reset file on tab switch
  }, [registrationType]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    showSuccessToastNotification(<p>Selected File: {selectedFile.name}</p>);
  };

  const registrationsClosed = false; // Toggled to false for testing, set back to true if needed

  let reCaptchaRef = useRef(null);
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone) => {
    return phone && phone.length >= 10;
  };
  const changeFormState = (args) => {
    setFormState((prevState) => ({
      ...prevState,
      [args.key]: args.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validation Logic
    if (registrationType === "student") {
      const validation = validateRegForm(formState);
      if (validation.status === false) {
        showWarnToastNotification(validation.message);
        return;
      }
    } else {
      // Basic validation for non-student forms
      if (!formState.name || !formState.email || !formState.phone) {
        showWarnToastNotification(<p>Please fill in all required fields.</p>);
        return;
      }

      if (!isValidEmail(formState.email)) {
        showWarnToastNotification(<p>Please enter a valid email address.</p>);
        return;
      }

      if (!isValidPhone(formState.phone)) {
        showWarnToastNotification(<p>Please enter a valid phone number.</p>);
        return;
      }
    }

    // 2. File Validation (Only if payment is required/student)
    if (paymentReq && (!file || !file.name.match(/\.(jpg|jpeg|png|gif)$/))) {
      showWarnToastNotification(<p>Please upload Screenshot of Payment</p>);
      return;
    }

    // 3. Captcha Validation
    if (reCaptchaRef.current.getValue() === "") {
      showWarnToastNotification(<p>Please select reCaptcha</p>);
      return;
    }

    setLoading(true);

    const formData = new FormData();
    // Append Form Data
    Object.entries(formState).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add type specific identifier if your backend needs it
    formData.append("registration_type", registrationType);

    if (file) {
      formData.append("file", file);
    }

    formData.append("captcha", reCaptchaRef.current.getValue());

    try {
      // Note: You might need different API endpoints for different types
      // or handle the 'registration_type' in your single backend endpoint.
      const resp = await apiRegisterUser(formData);

      if (resp.status === 201 || resp.status === 200) {
        showSuccessToastNotification(
          resp.data.message || "Registered Successfully",
        );

        // Reset Form
        setFormState(
          registrationType === "student"
            ? studentFormFormat
            : partnerFormFormat,
        );
        setFile(null);
      } else {
        showErrorToastNotification(resp.data.message || "Registration failed");
      }
    } catch (error) {
      console.error("Error:", error);
      showErrorToastNotification(
        "Registration failed. Please try again later.",
      );
    }

    reCaptchaRef.current.reset();
    setLoading(false);
  };

  // Helper to render the Tab Buttons
  const renderTabs = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "10px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      {["student", "college", "sponsor", "company"].map((type) => (
        <button
          key={type}
          type="button"
          onClick={() => setRegistrationType(type)}
          style={{
            padding: "8px 16px",
            backgroundColor:
              registrationType === type ? "#fff" : "rgba(255,255,255,0.1)",
            color: registrationType === type ? "#000" : "#fff",
            border: "1px solid #fff",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "0.9rem",
            fontWeight: "600",
            textTransform: "capitalize",
            transition: "all 0.3s ease",
          }}
        >
          {type}
        </button>
      ))}
    </div>
  );

  return (
    <div className={`${styles.wrapper}`}>
      <div className={`${styles.title}`}>
        <h2>Register for Xceed'25</h2>
      </div>

      <div className={styles.contentContainer}>
        {/* Left pane - Form */}
        {loading && (
          <div className={styles.loader_container}>
            <SimpleLoader text="Registering..." />
          </div>
        )}

        <form
          className={`${styles.formContent}`}
          style={{ display: loading ? "none" : "flex" }}
        >
          {/* Render Tabs */}
          {renderTabs()}

          {/* Conditional Header: Only for Students */}
          {registrationType === "student" && (
            <div className={styles.formHeader}>Get your XCEED ID here!</div>
          )}

          <a className={styles.infoLink} href="#regInfo">
            <div className={`${styles.button}`}>Click here for more info</div>
          </a>

          {/* --- STUDENT FORM FIELDS --- */}
          {registrationType === "student" && (
            <>
              <FormField
                type="text"
                fieldIcon={nameIcon}
                placeholder="Full name"
                name="name"
                value={formState}
                setter={changeFormState}
              />
              <FormField
                type="text"
                fieldIcon={deptIcon}
                placeholder="Department"
                name="dept"
                value={formState}
                setter={changeFormState}
              />
              <FormField
                type="dropdown"
                dropdownValues={[
                  { name: "1st year", value: 1 },
                  { name: "2nd year", value: 2 },
                  { name: "3rd year", value: 3 },
                  { name: "4th year", value: 4 },
                  { name: "5th year", value: 5 },
                ]}
                fieldIcon={yearIcon}
                placeholder="Year"
                name="year"
                value={formState}
                setter={changeFormState}
              />
              <FormField
                type="text"
                fieldIcon={collegeIcon}
                placeholder="College"
                name="college"
                value={formState}
                setter={changeFormState}
              />
              <FormField
                type="email"
                fieldIcon={emailIcon}
                placeholder="E-mail ID"
                name="email"
                value={formState}
                setter={changeFormState}
              />
              <FormField
                type="phone"
                fieldIcon={phoneIcon}
                placeholder="Phone number"
                name="phone"
                value={formState}
                setter={changeFormState}
              />
              <FormField
                type="dropdown"
                dropdownValues={[
                  // Workshops
                  {
                    name: "Transmission Systems Workshop",
                    value: "TRANSMISSION_SYSTEMS",
                  },
                  {
                    name: "IoT at Your Ease Workshop",
                    value: "IOT_EASE",
                  },
                  {
                    name: "All You Want to Know About Drones Workshop",
                    value: "DRONES_WORKSHOP",
                  },

                  // Events
                  {
                    name: "Webcraft",
                    value: "WEBCRAFT",
                  },
                  {
                    name: "OSPC",
                    value: "OSPC",
                  },
                  {
                    name: "Brass and Brains",
                    value: "BRASS_BRAINS",
                  },
                  {
                    name: "ElectroHunt",
                    value: "ELECTROHUNT",
                  },
                  
                ]}
                fieldIcon={yearIcon}
                placeholder="Workshops and Events"
                name="workshopsandevents"
                value={formState}
                setter={changeFormState}
              />
            </>
          )}

          {/* --- OTHER FORMS (College, Sponsor, Company) --- */}
          {registrationType !== "student" && (
            <>
              <div
                className={styles.formHeader}
                style={{ fontSize: "1.2rem", marginTop: "0" }}
              >
                {registrationType.charAt(0).toUpperCase() +
                  registrationType.slice(1)}{" "}
                Registration
              </div>

              <FormField
                type="text"
                fieldIcon={nameIcon}
                placeholder={
                  registrationType === "college"
                    ? "College Name"
                    : registrationType === "company"
                      ? "Company Name"
                      : "Sponsor Name"
                }
                name="name"
                value={formState}
                setter={changeFormState}
              />

              <FormField
                type="text" // Assuming FormField handles text area or long text gracefully, otherwise might need a custom TextArea component
                fieldIcon={descriptionIcon}
                placeholder="Description"
                name="description"
                value={formState}
                setter={changeFormState}
              />

              <FormField
                type="email"
                fieldIcon={emailIcon}
                placeholder="Contact E-mail"
                name="email"
                value={formState}
                setter={changeFormState}
              />

              <FormField
                type="phone"
                fieldIcon={phoneIcon}
                placeholder="Contact Phone"
                name="phone"
                value={formState}
                setter={changeFormState}
              />
            </>
          )}

          {/* --- PAYMENT UPLOAD (Conditionally Rendered) --- */}
          {/* Logic: Only show if needed. Typically sponsors/companies might not need this flow here */}
          {paymentReq && registrationType === "student" && (
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{ margin: "10px 0" }}
            >
              Upload Screenshot of Payment
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileChange}
                accept="image/*"
              />
            </Button>
          )}
          {file && (
            <p style={{ color: "#fff", fontSize: "0.9rem" }}>
              Selected File: {file.name}
            </p>
          )}

          <br />

          <ReCAPTCHA
            sitekey={process.env.REACT_APP_CAPTCHA_KEY}
            theme="dark"
            size="normal"
            className={`${styles.recaptcha}`}
            ref={reCaptchaRef}
          />

          <div
            id="regInfo"
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <div
              className={`${styles.button} ${registrationsClosed ? styles.disabled : ""}`}
              onClick={!registrationsClosed ? handleSubmit : null}
              style={{
                cursor: registrationsClosed ? "not-allowed" : "pointer",
                opacity: registrationsClosed ? 0.5 : 1,
                marginTop: "15px",
              }}
            >
              {registrationsClosed ? "Registrations Closed" : "Register"}
            </div>
          </div>
        </form>

        {/* Right pane - info */}
        <RegistrationInfoPane />
      </div>
    </div>
  );
};

export default Registration;
